import { useEffect } from "react";

const SITE = "https://gameplatplay.com";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function usePageMeta(page) {
  useEffect(() => {
    if (!page) return undefined;
    const prevTitle = document.title;
    const url = `${SITE}${page.path || window.location.pathname}`;
    const title = page.title || prevTitle;
    const description = page.description || "";
    const isArticle = page.kind === "guide";

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: page.indexable === false ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: isArticle ? "article" : "website" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertLink("canonical", url);

    let jsonld = document.getElementById("gp-jsonld");
    const graph = [];

    if (isArticle) {
      graph.push({
        "@type": "Article",
        headline: page.h1 || title,
        description,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        publisher: { "@type": "Organization", name: "GamePlatPlay", url: SITE },
      });
    }

    if (page.faq?.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: page.faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      });
    }

    if (graph.length) {
      if (!jsonld) {
        jsonld = document.createElement("script");
        jsonld.id = "gp-jsonld";
        jsonld.type = "application/ld+json";
        document.head.appendChild(jsonld);
      }
      jsonld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": graph,
      });
    }

    return () => {
      document.title = prevTitle;
      if (jsonld && jsonld.parentNode) jsonld.parentNode.removeChild(jsonld);
    };
  }, [page]);
}
